import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { useReactFlow, XYPosition } from 'reactflow';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import cloneDeep from 'lodash/cloneDeep';
import { isNil } from 'lodash';
import { log } from '@/logger/logger.ts';
import { Node, Edge, BaseNodeData } from '@/types/node';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useSubscriptionPermissions } from '@/hooks/useSubscriptionPermissions/useSubscriptionPermissions';
import { I18N_KEYS } from '@/language/keys';
import { SubscriptionType } from '@/types/shared';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore, useUserWorkflowRole } from '@/state/workflow.state';
import { getCenterScreenPosition } from '@/hooks/useReactFlowViewportPositions';
import { useNodeDuplication } from '@/hooks/useNodeDuplication';
import { NodeType } from '@/enums/node-type.enum';
import { IMPORT_MODEL_NODE_ID, PROMPT_NODE_ID } from '@/consts/node-types.consts';
import { FF_FIGMA_COMPOSITOR } from '@/consts/featureFlags';
import { isValidModelImportIdentifier } from '@/utils/model-import.utils';
import { truncateStrIfNeeded } from '@/utils/strings.utils';
import { useMousePosition } from '@/hooks/useMousePosition';
import { SubflowTemplateType } from '@/types/api/subflowTemplate';
import { PROMPT_MAX_CHARS } from '../Nodes/consts/validations.consts';
import { getValidPastedFigmaNodes, PASTED_FIGMA_NODE_OFFSET } from '../Nodes/FigmaCompositor/utils';
import { AddNewNodeOptions, NodePlacement } from './flowTypes';
import { useEdges, useFlowView, useNodes } from './FlowContext';
import { isPasteFromEditableElement } from './utils';
import type { NodeDefinition } from '@/types/api/nodeDefinition';

const logger = log.getLogger('useGraph');

// TODO remove and use NodePlacement directly at call sites
function placementFromNewNodeArgs({
  dropX,
  dropY,
  fromCenter,
  absolutePosition,
}: Pick<AddNewNodeOptions, 'dropX' | 'dropY' | 'fromCenter' | 'absolutePosition'>): NodePlacement {
  if (typeof dropX === 'number' && typeof dropY === 'number') {
    if (fromCenter) {
      return { type: 'fromCenter', dx: dropX, dy: dropY };
    } else {
      return absolutePosition ? { type: 'absolute', x: dropX, y: dropY } : { type: 'drop', x: dropX, y: dropY };
    }
  } else {
    return { type: 'atCenter' };
  }
}

function getNewNodePosition(
  flowContainerRect: DOMRect,
  screenToFlowPosition: (p: XYPosition) => XYPosition,
  placement: NodePlacement,
): XYPosition {
  switch (placement.type) {
    case 'fromCenter': {
      const centerPosition = getCenterScreenPosition(flowContainerRect, screenToFlowPosition);
      return { x: centerPosition.x + placement.dx, y: centerPosition.y + placement.dy };
    }
    case 'absolute': {
      return { x: placement.x, y: placement.y };
    }
    case 'drop': {
      const flowPosition = screenToFlowPosition({
        x: placement.x,
        y: placement.y,
      });
      return flowPosition;
    }
    case 'atCenter': {
      return getCenterScreenPosition(flowContainerRect, screenToFlowPosition);
    }
    default: {
      const _exhaustiveCheck: never = placement;
      throw new Error('invalid node placement');
    }
  }
}

function createNodeFromTemplate(action: AddNewNodeOptions['action'], position: XYPosition): Node | undefined {
  const savedNode = action.savedTemplateData?.nodes[0];
  if (!savedNode) return undefined;
  const clone = cloneDeep(savedNode);
  return {
    ...clone,
    id: uuidv4(),
    position,
    parentId: undefined,
    locked: false,
    selected: false,
    createdAt: new Date().toISOString(),
    data: { ...clone.data, name: action.name ?? savedNode.data.name },
  };
}

export function createNewNode(
  nodeTypeId: string,
  nodeTypes: NodeDefinition[],
  position: XYPosition,
  initialData?: any,
  pastedData?: any,
  options?: AddNewNodeOptions['options'],
) {
  const selectedOption = nodeTypes.find((option) => option.id === nodeTypeId);
  if (selectedOption) {
    // Create a new node object with the selected option's data
    const { metadata: _metadata, ...selectedOptionWithoutMetadata } = selectedOption;
    const newNode: Node = {
      ...selectedOptionWithoutMetadata,
      locked: selectedOptionWithoutMetadata.data.isLocked ?? false,
      parentId: undefined,
      id: uuidv4(),
      position,
      createdAt: new Date().toISOString(),
      data: {
        ...cloneDeep(selectedOptionWithoutMetadata.data),
        ...initialData,
        ...(initialData ? { initialData } : {}),
        ...(pastedData ? { pastedData } : {}),
      } as BaseNodeData, // FIXME
    };

    if (options?.isClearInitialData) {
      delete newNode.data.initialData;
    }
    return newNode;
  }
}

export function useGraph() {
  const { t } = useTranslation();
  const role = useUserWorkflowRole();
  const { screenToFlowPosition } = useReactFlow();
  const flowView = useFlowView();
  const { track } = useAnalytics();
  const { isSubscriptionTypeIn } = useSubscriptionPermissions();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const getReactFlowContainerRect = useWorkflowStore((s) => s.getReactFlowContainerRect);
  const mousePosition = useMousePosition();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const isFigmaCompositorEnabled = useFeatureFlagEnabled(FF_FIGMA_COMPOSITOR);

  const addNewNode = useCallback(
    ({
      action,
      dropX,
      dropY,
      absolutePosition = false,
      fromCenter = false,
      connection,
      options,
    }: AddNewNodeOptions) => {
      track(
        'added_new_node',
        {
          type: action.displayName,
          definitionType: action.type || nodeTypes.find((n) => n.id === action.id)?.type,
          nodeDefinitionId: action.id,
        },
        TrackTypeEnum.BI,
      );
      const reactFlowContainerRect = getReactFlowContainerRect();
      if (reactFlowContainerRect === undefined) throw new Error('Expected Flow container');

      const placement = placementFromNewNodeArgs({ dropX, dropY, absolutePosition, fromCenter });
      const position = getNewNodePosition(reactFlowContainerRect, screenToFlowPosition, placement);
      const isSubflowTemplate =
        action.type === SubflowTemplateType.SavedNode ||
        action.type === SubflowTemplateType.SavedModel ||
        action.type === SubflowTemplateType.Subflow;
      const newNode = isSubflowTemplate
        ? createNodeFromTemplate(action, position)
        : createNewNode(action.id, nodeTypes, position, action.initialData, action.pastedData, options);
      if (newNode) {
        flowView.addNewNode(newNode, connection);
        flowView.selectedNodes = [newNode.id];
        return newNode;
      } else {
        logger.error(`Option for action "${action.name}" not found.`);
      }
    },
    [flowView, nodeTypes, getReactFlowContainerRect, screenToFlowPosition, track],
  );

  const { positionDuplicatedNodes, scrollNodeIntoViewIfNeeded } = useNodeDuplication();

  const createPromptNode = useCallback(
    (pastedText: string) => {
      const { value, isTruncated } = truncateStrIfNeeded(pastedText.trim(), PROMPT_MAX_CHARS);
      if (isTruncated) {
        updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.NODES.PROMPT_NODE_V3.TRUNCATED_WARNING, { max: PROMPT_MAX_CHARS }),
          severity: 'error',
          icon: null,
          isOpen: true,
        });
      }
      addNewNode({
        action: {
          id: PROMPT_NODE_ID,
          // We use initialData as we can simply override the prompt. We discard it immediately after creation.
          initialData: { prompt: value },
          type: 'prompt_pasted_text',
        },
        options: { isClearInitialData: true },
      });
    },
    [addNewNode, t, updateSnackbarData],
  );

  const tryPasteFigmaCompositorNodes = useCallback(
    (html: string): boolean => {
      const nodes = getValidPastedFigmaNodes(html, !!isFigmaCompositorEnabled);
      if (nodes.length === 0) return false;
      const reactFlowContainerRect = getReactFlowContainerRect();
      if (!reactFlowContainerRect) return false;
      const baseX = mousePosition.current.x || reactFlowContainerRect.width / 2 + reactFlowContainerRect.left;
      const baseY = mousePosition.current.y || reactFlowContainerRect.height / 2 + reactFlowContainerRect.top;
      nodes.forEach(({ fileKey, nodeId }, index) => {
        addNewNode({
          action: { initialData: { options: { fileKey, nodeId } }, id: 'figma_compositor' },
          dropX: baseX + index * PASTED_FIGMA_NODE_OFFSET,
          dropY: baseY,
        });
      });
      return true;
    },
    [addNewNode, getReactFlowContainerRect, isFigmaCompositorEnabled, mousePosition],
  );

  const pasteNodesOrImageFromClipboard = useCallback(
    (event: ClipboardEvent) => {
      if (role !== 'editor' || isPasteFromEditableElement(event)) {
        return;
      }

      const clipboardData = event.clipboardData;
      if (!clipboardData) throw new Error('Expected browser paste event to always have clipboardData');

      const html = event.clipboardData.getData('text/html');

      // We want to return early if a figma compositor node has successfully been added
      // This way we skip other content being pasted, ex. the raw text
      if (tryPasteFigmaCompositorNodes(html)) return;

      // Check for image files first (including macOS screen captures)
      for (const item of clipboardData.items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) {
            logger.info('paste image from clipboard', { file });
            addNewNode({
              action: {
                type: 'import',
                pastedData: { imageFile: file },
                id: 'wkKkBSd0yrZGwbStnU6rClone',
              },
            });
          }
          return;
        }
      }

      const pastedText = clipboardData.getData('text/plain');
      if (!pastedText) return;

      // handle paste nodes and edges
      let parsedJson;
      try {
        parsedJson = JSON.parse(pastedText);
      } catch (_e) {
        const isValidModelImportUrl = isValidModelImportIdentifier(pastedText);
        if (isValidModelImportUrl) {
          if (isSubscriptionTypeIn([SubscriptionType.Enterprise])) {
            return logger.warn('Model import is not supported for enterprise users', { pastedText });
          }
          return addNewNode({
            action: {
              id: IMPORT_MODEL_NODE_ID,
              // We use pastedData as we need it inside the node. We then discard it
              pastedData: { importedModelIdentifier: pastedText },
            },
          });
        }

        return createPromptNode(pastedText);
      }

      if (isNil(parsedJson)) return;

      const clipboardNodes: Node[] | undefined = parsedJson.nodes;
      const clipboardEdges: Edge[] | undefined = parsedJson.edges;

      if (clipboardNodes && clipboardNodes.length > 0) {
        flowView.pasteAndPositionNodesAndEdges(
          clipboardNodes,
          clipboardEdges ?? [],
          positionDuplicatedNodes,
          scrollNodeIntoViewIfNeeded,
        );
      } else {
        // json string - create prompt node also
        return createPromptNode(pastedText);
      }
    },
    [
      role,
      addNewNode,
      createPromptNode,
      isSubscriptionTypeIn,
      flowView,
      positionDuplicatedNodes,
      scrollNodeIntoViewIfNeeded,
      tryPasteFigmaCompositorNodes,
    ],
  );

  const nodes = useNodes();
  const edges = useEdges();
  const copyNodesToClipboard = useCallback(() => {
    const selectedNodes = nodes.filter((n) => n.selected);
    if (selectedNodes.length > 0) {
      const idsOfSelectedNodes = new Set(selectedNodes.map((n) => n.id));

      const nodesIncludingChildren = selectedNodes.flatMap((node) => {
        if (node.type === NodeType.CustomGroup) {
          const nonSelectedChildren = flowView
            .getChildNodesOfGroup(node.id)
            .filter((childNode) => !idsOfSelectedNodes.has(childNode.id));
          return [node, ...nonSelectedChildren];
        }
        return node;
      });

      const nodesIncludingChildrenIdSet = new Set(nodesIncludingChildren.map((node) => node.id));

      // Copy edges that connect between selected nodes
      const edgesToCopy = edges.filter(
        (edge) => nodesIncludingChildrenIdSet.has(edge.source) && nodesIncludingChildrenIdSet.has(edge.target),
      );

      // setClipboardNodes(nodesToCopy);
      // setClipboardEdges(edgesToCopy); // Store copied edges as is
      const clipboardDataString = JSON.stringify({ nodes: nodesIncludingChildren, edges: edgesToCopy });
      void navigator.clipboard.writeText(clipboardDataString);
    }
  }, [edges, nodes, flowView]);

  return {
    addNewNode,
    copyNodesToClipboard,
    pasteNodesOrImageFromClipboard,
  };
}
